/* eslint-disable array-callback-return */
import React from 'react'
import { Q } from '@nozbe/watermelondb'
import moment from 'moment'
import compact from 'lodash/compact';
import filter from 'lodash/filter';
import find from 'lodash/find';
import flattenDeep from 'lodash/flattenDeep';
import isEmpty from 'lodash/isEmpty';
import isNumber from 'lodash/isNumber';
import partition from 'lodash/partition';
import uniqBy from 'lodash/uniqBy';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { AVAILABLE_DTO_MAPPINGS, AVAILABLE_ENTITIES } from './constants'
import { LOTE, formatName } from '../../utils/formHelper'
import { toDTO } from '../resources/autoMapping'
import Repository from '../infra/repository'
import database from '../index'
import { recordEvent } from '../../observability/events'

const MSG = {
  NOT_FOUND: 'Matriz não encontrada.',
  PREGNANT: 'Matriz prenhe em DG anterior.',
  MORE_THAN_ONE_INSIDE_BATCH:
    'Existe mais de uma matriz que atende o critério de busca diagnosticadas como prenhe em DG anterior.',
  NOT_IN_BATCH: (codVaca, nomeLote, protocolName, dia) =>
    `A matriz ${codVaca} não faz parte deste Lote. Este animal se encontra no ${formatName(
      nomeLote,
      LOTE
    )} com o Protocolo ${protocolName} e no manejo ${dia}.`,
  MORE_THAN_ONE_OUTSIDE_BATCH: (cowCode) =>
    `A matriz ${cowCode} não faz parte deste Lote. Há mais de um animal registrado com esse ID.`,
}

export async function getFromLocalStorage(key) {
  return database.adapter.getLocal(key)
}

export async function setInLocalStorage(key, value) {
  return database.adapter.setLocal(key, value)
}

export async function removeFromLocalStorage(key) {
  return database.adapter.removeLocal(key)
}

// Cache em memória por sessão de navegação: domain_values só mudam via sync,
// então o resultado por tipo é estável entre syncs. Invalidado no evento
// 'progerar:sync-completed' (disparado ao fim de cada ciclo de sync).
// Páginas IATF (ex.: D0) consultam ~10 tipos por navegação — sem cache,
// cada navegação refaz todas as queries.
const _domainValuesCache = new Map() // domainTypeName -> DTO[]

export function invalidateDomainValuesCache() {
  _domainValuesCache.clear()
}

if (typeof window !== 'undefined') {
  window.addEventListener('progerar:sync-completed', invalidateDomainValuesCache)
}

export async function getDomainValuesBy(domainTypeName) {
  if (_domainValuesCache.has(domainTypeName)) {
    // Cópia rasa: protege o cache de sort/splice feitos por callers.
    return [..._domainValuesCache.get(domainTypeName)]
  }
  const valuesRepository = new Repository(AVAILABLE_ENTITIES.DOMAIN_VALUES)
  const domainValues = await valuesRepository.getByRelation(
    'domain_types',
    'nome',
    domainTypeName
  )
  if (domainValues.success) {
    _domainValuesCache.set(domainTypeName, domainValues.response)
    return [...domainValues.response]
  }
  console.warn(
    `There was a problem retrieving DomainValues for DomainType ${domainTypeName}: ${domainValues.exception}`
  )
  return []
}

export async function getDomainValuesById(domainTypeId) {
  const valuesRepository = new Repository(AVAILABLE_ENTITIES.DOMAIN_VALUES)
  const domainValues = await valuesRepository.getByParam(
    'id',
    domainTypeId
  )
  if (domainValues.success) {
    return domainValues.response
  }
  console.warn(
    `There was a problem retrieving DomainValues for DomainType ${domainTypeId}: ${domainValues.exception}`
  )
  return []
}

export async function getDomainValueByDomainValueParent(parentId) {
  const valuesRepository = new Repository(AVAILABLE_ENTITIES.DOMAIN_VALUES)
  const domainValues = await valuesRepository.getByParam(
    'valor_dominio_pai_id',
    parentId
  )
  if (domainValues.success) {
    return domainValues.response
  }
  console.warn(
    `There was a problem retrieving DomainValues with parentId ${parentId}: ${domainValues.exception}`
  )
  return []
}

export async function getChildren(parent) {
  const children = await Promise.all(
    parent.map((element) => getDomainValueByDomainValueParent(element.id))
  )
  return compact(flattenDeep(children))
}

// Monta DTOs de protocolo com filhos a partir de entidades já buscadas.
// Semântica igual ao Repository._fetchChildren: filhos deletados descartados.
function assembleProtocolsWithChildren(protocolEntities, mpEntities) {
  const protocolMapping = AVAILABLE_DTO_MAPPINGS[AVAILABLE_ENTITIES.PROTOCOLS]
  const mpMapping =
    AVAILABLE_DTO_MAPPINGS[AVAILABLE_ENTITIES.MANAGEMENT_PROTOCOLS]

  const mpsByProtocolId = new Map()
  for (const mp of mpEntities) {
    if (mp.isDeleted) {
      continue
    }
    const dto = toDTO(mpMapping, mp)
    const pid = dto.managementProtocolId // = protocol_id (ver ManagementProtocols)
    if (!mpsByProtocolId.has(pid)) {
      mpsByProtocolId.set(pid, [])
    }
    mpsByProtocolId.get(pid).push(dto)
  }

  return protocolEntities.map((entity) => ({
    ...toDTO(protocolMapping, entity),
    managementProtocols: mpsByProtocolId.get(entity.id) || [],
  }))
}

// Busca protocolos + seus manejos em 2 queries no TOTAL, em vez do padrão
// Repository._fetchChildren que dispara 1 query de filhos POR protocolo
// (N+1 — em fazendas grandes, 20-50 queries só para montar filtros).
// includeDeleted controla o filtro do pai (getById não filtra, get() filtra).
export async function getProtocolsWithChildrenByIds(
  protocolIds,
  { includeDeleted = false } = {}
) {
  if (!protocolIds.length) {
    return []
  }
  const protocolClauses = [Q.where('id', Q.oneOf(protocolIds))]
  if (!includeDeleted) {
    protocolClauses.push(Q.where('isDeleted', false))
  }

  const [protocolEntities, mpEntities] = await Promise.all([
    database.collections
      .get('protocols')
      .query(...protocolClauses)
      .fetch(),
    database.collections
      .get('management_protocols')
      .query(Q.where('protocol_id', Q.oneOf(protocolIds)))
      .fetch(),
  ])

  return assembleProtocolsWithChildren(protocolEntities, mpEntities)
}

// Variante "todos": substitui getAll(PROTOCOLS) — que disparava 1 query de
// manejos POR protocolo do banco (N+1) — nas telas de criação/edição de lote
// e ressincronização. Mesma semântica do Repository.get(): pais com
// isDeleted=false, filhos deletados descartados.
export async function getAllProtocolsWithChildren() {
  const [protocolEntities, mpEntities] = await Promise.all([
    database.collections
      .get('protocols')
      .query(Q.where('isDeleted', false))
      .fetch(),
    database.collections.get('management_protocols').query().fetch(),
  ])

  return assembleProtocolsWithChildren(protocolEntities, mpEntities)
}

export async function getElement(entity, id) {
  const repository = new Repository(entity)
  const request = await repository.getById(id)
  if (request.success) {
    return request.response
  }
  return null
}

export async function getAll(entity) {
  const repository = new Repository(entity)
  const request = await repository.get()
  if (request.success) {
    return request.response
  }
  return []
}

export async function getWithParam(entity, column, param) {
  const repository = new Repository(entity)
  const request = await repository.getByParam(column, param)
  if (request.success) {
    return request.response
  }
  return []
}

export async function getWithParamWithoutDeletedTrue(entity, column, param) {
  const repository = new Repository(entity)
  const request = await repository.getByParamDeleted(column, param)
  if (request.success) {
    return request.response
  }
  return []
}

export async function getWithRelation(entity, relation, column, param) {
  const repository = new Repository(entity)
  const request = await repository.getByRelation(relation, column, param)
  if (request.success) {
    return request.response
  }
  return []
}

/**
 * HOC que injeta props do banco de dados local em um componente.
 *
 * Melhorias (2025-12-12):
 * - Adiciona timeout para evitar bloqueio indefinido
 * - Mostra indicador de loading enquanto carrega dados
 * - Suporte para modo offline com fallback
 * - Tratamento de erros
 *
 * @param {Function} retrievePropsFunction - Função async que retorna props do DB
 * @param {React.Component} WrappedComponent - Componente a ser wrapped
 * @param {Object} options - Opções de configuração
 * @param {number} options.timeout - Timeout em ms (default: 5000)
 * @param {boolean} options.showLoading - Mostrar loading spinner (default: true)
 *
 * Ref: docs/INVESTIGACAO_COMPLETA_BUG_OFFLINE.md - CAUSA #4
 */
export const withPropsFromDatabase = (
  retrievePropsFunction,
  WrappedComponent,
  options = {}
) => {
  const { timeout = 5000, showLoading = true } = options;

  class WithPropsFromDatabase extends React.Component {
    state = {
      loading: true,
      error: null,
      dbProps: null,
      isOffline: !window.navigator.onLine,
      slowWarning: false,
    }

    _isMounted = false;
    _offlineHandler = null;
    _onlineHandler = null;
    _softTimer = null;
    _hardTimer = null;

    componentDidMount() {
      this._isMounted = true;
      this._options = options;

      // Listeners para mudança de conectividade
      this._offlineHandler = () => {
        if (this._isMounted) {
          this.setState({ isOffline: true });
        }
      };
      this._onlineHandler = () => {
        if (this._isMounted) {
          this.setState({ isOffline: false });
        }
      };

      window.addEventListener('offline', this._offlineHandler);
      window.addEventListener('online', this._onlineHandler);

      this.loadProps();
    }

    componentWillUnmount() {
      this._isMounted = false;
      clearTimeout(this._softTimer);
      clearTimeout(this._hardTimer);
      window.removeEventListener('offline', this._offlineHandler);
      window.removeEventListener('online', this._onlineHandler);
    }

    async loadProps() {
      const { timeout: softTimeout = 5000, hardTimeout = 3 * 60 * 1000 } = this._options || {};

      // Reset do aviso lento para o caso de retry
      if (this._isMounted) {
        this.setState({ slowWarning: false });
      }

      this._softTimer = setTimeout(() => {
        recordEvent({
          level: 'warn',
          category: 'outro',
          action: 'db_props_slow',
          payload: { pathname: window.location.pathname },
        });
        if (this._isMounted) {
          this.setState({ slowWarning: true });
        }
      }, softTimeout);

      try {
        // Race entre a função e o hard ceiling
        const dbProps = await Promise.race([
          retrievePropsFunction(this.props),
          new Promise((_, reject) => {
            this._hardTimer = setTimeout(
              () => reject(new Error('Database query hard timeout')),
              hardTimeout
            );
          }),
        ]);

        clearTimeout(this._softTimer);
        clearTimeout(this._hardTimer);

        if (this._isMounted) {
          this.setState({ dbProps, loading: false, error: null, slowWarning: false });
        }
      } catch (error) {
        clearTimeout(this._softTimer);
        clearTimeout(this._hardTimer);

        console.error('[WITH-PROPS-DB] Error loading props:', error);

        if (this._isMounted) {
          // Em modo offline, tentar continuar mesmo com erro
          if (!window.navigator.onLine) {
            console.warn('[WITH-PROPS-DB] Offline mode - continuing with empty props');
            this.setState({
              dbProps: {},
              loading: false,
              error: null,
              isOffline: true,
            });
          } else {
            this.setState({ loading: false, error: error.message });
          }
        }
      }
    }

    render() {
      const { loading, error, dbProps, isOffline, slowWarning } = this.state;

      // Loading state - mostrar spinner ou nada
      if (loading) {
        if (showLoading) {
          return (
            <div style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              height: '100%',
              minHeight: '200px',
              flexDirection: 'column',
              gap: '10px'
            }}>
              <div style={{
                width: '40px',
                height: '40px',
                border: '3px solid #f3f3f3',
                borderTop: '3px solid #ff661c',
                borderRadius: '50%',
                animation: 'spin 1s linear infinite'
              }} />
              {isOffline && (
                <span style={{ color: '#666', fontSize: '14px' }}>
                  Modo offline - carregando dados locais...
                </span>
              )}
              {slowWarning && (
                <span style={{ color: '#888', fontSize: '13px', textAlign: 'center', maxWidth: '280px' }}>
                  Está demorando mais que o normal — fazendas grandes podem levar alguns minutos. Aguarde...
                </span>
              )}
              <style>{`
                @keyframes spin {
                  0% { transform: rotate(0deg); }
                  100% { transform: rotate(360deg); }
                }
              `}</style>
            </div>
          );
        }
        return null;
      }

      // Error state - apenas em modo online (offline tem fallback)
      if (error && !isOffline) {
        return (
          <div style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '100%',
            minHeight: '200px',
            flexDirection: 'column',
            gap: '10px',
            padding: '20px'
          }}>
            <span style={{ color: '#d32f2f', fontSize: '16px' }}>
              Não foi possível carregar os dados
            </span>
            <span style={{ color: '#666', fontSize: '14px' }}>
              A operação demorou muito. Verifique sua conexão e tente novamente.
            </span>
            <button
              onClick={() => {
                this.setState({ loading: true, error: null, slowWarning: false });
                this.loadProps();
              }}
              style={{
                marginTop: '10px',
                padding: '8px 16px',
                backgroundColor: '#ff661c',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer'
              }}
            >
              Tentar novamente
            </button>
          </div>
        );
      }

      // Success - render wrapped component
      const newProps = {
        ...this.props,
        ...dbProps,
        _isOffline: isOffline
      }

      return <WrappedComponent {...newProps} />;
    }
  }

  // react-router-dom v7: <Route element={<X/>}> NÃO injeta automaticamente
  // history/match/location nas props como v5 fazia. Wrappamos a class
  // component em um function component que consome useNavigate/useParams/
  // useLocation e injeta as props com a mesma shape do v5. Isso permite que
  // retrievePropsFunction continue lendo `props.match.params.X` sem
  // mudar uma linha nos 25 arquivos consumidores.
  function WithRouterPropsFromDatabase(props) {
    const navigate = useNavigate();
    const params = useParams();
    const location = useLocation();
    const history = {
      push: (to, state) =>
        typeof to === 'string'
          ? navigate(to, { state })
          : navigate(to.pathname ?? to, { state: to.state ?? state }),
      replace: (to, state) =>
        typeof to === 'string'
          ? navigate(to, { replace: true, state })
          : navigate(to.pathname ?? to, { replace: true, state: to.state ?? state }),
      goBack: () => navigate(-1),
      go: (delta) => navigate(delta),
      location,
    };
    const match = {
      params,
      path: location.pathname,
      url: location.pathname,
      isExact: true,
    };
    return <WithPropsFromDatabase {...props} history={history} location={location} match={match} />;
  }
  WithRouterPropsFromDatabase.displayName = `withPropsFromDatabase(${WrappedComponent.displayName || WrappedComponent.name || 'Component'})`;
  return WithRouterPropsFromDatabase;
}

export async function farmWideSearch(cowCode, selectedBatchesIds, farmId) {
  let result = {
    data: null,
    msg: null,
  }

  // 1. Check all corrals that compose given farm
  const corrals = await getWithParam(
    AVAILABLE_ENTITIES.CORRALS,
    'farm_id',
    farmId
  )

  // 2. Use previous information to retrieve all batches from that farm
  const batches = await getWithParam(
    AVAILABLE_ENTITIES.BATCHES,
    'corral_id',
    Q.oneOf(corrals.map((corral) => corral.id))
  )

  // 3. Isolate originalBatches of selectedBatchesIds, so we can proper partition cows into "in-batch" and "out-batch"
  const originalBatches = selectedBatchesIds.map((batchId) => {
    const batch = find(batches, { id: batchId })
    if (batch.originalBatchId !== null) {
      return batch.originalBatchId
    }
    return batchId
  })

  // 4. Retrieve all batches related to the original ones we isolated
  const relatedBatches = filter(
    batches,
    (batch) =>
      originalBatches.indexOf(batch.id) >= 0 ||
      originalBatches.indexOf(batch.originalBatchId) >= 0
  )

  // 5. Retrieve all cows from that farm that are possible candidates: (omit_from_dg_final === false)
  const cowsCollection = database.collections.get(
    AVAILABLE_ENTITIES.D0S.toLowerCase()
  )

  const cows = (
    await cowsCollection
      .query(
        Q.where('batch_id', Q.oneOf(batches.map((batch) => batch.id))),
        Q.where('codVaca', Q.like(Q.sanitizeLikeString(cowCode.trim()))),
        Q.where('isDeleted', false),
        Q.where('omit_from_dg_final', false)
      )
      .fetch()
  ).map((entity) =>
    toDTO(AVAILABLE_DTO_MAPPINGS[AVAILABLE_ENTITIES.D0S], entity)
  )

  // 6. If no result is given, we can return now
  if (cows.length === 0) {
    result.msg = MSG.NOT_FOUND
    return result
  }

  // 7. Separate cows according to their respective batches (if it belongs to any of related batches or not)
  const [cowsInBatch, cowsOutBatch] = partition(cows, (cow) =>
    find(relatedBatches, { id: cow.loteId })
  )

  // 8. Evaluate if there's any cow in relatedBatches (this case has precedence)
  if (cowsInBatch.length > 0) {
    result.data = cowsInBatch
    let pregCount = 0
    const prenheDG = (await getDomainValuesBy('Condição DG')).filter(
      (x) => x.valor.toLowerCase().indexOf('prenhe') > 0
    )
    const prenheDGFinal = (await getDomainValuesBy('Condição DG Final')).filter(
      (x) => x.valor.toLowerCase().indexOf('prenhe') > 0
    )
    for (const cow of cowsInBatch.filter(
      (cow) => selectedBatchesIds.indexOf(cow.loteId) === -1
    )) {
      const isPrenhe =
        prenheDG.find((x) => x.id === cow.condicaoDG) ||
        prenheDGFinal.find((x) => x.id === cow.condicaoDGFinal)

      if (isPrenhe) {
        pregCount++
      }

      if (pregCount >= 2) {
        break
      }
    }

    if (pregCount > 0) {
      result.msg =
        pregCount === 1 ? MSG.PREGNANT : MSG.MORE_THAN_ONE_INSIDE_BATCH
      return result
    }
  }

  const filteredCowsOutBatch = []

  cowsOutBatch.sort((a, b) => a.iatf - b.iatf)

  for (let cob of cowsOutBatch) {
    cob.firstId = cob.dzeroOriginal || cob.id
    if (filteredCowsOutBatch.length > 0) {
      filteredCowsOutBatch.map((fcob, index) => {
        if (fcob.firstId === cob.firstId) {
          if (cob.iatf > fcob.iatf) {
            filteredCowsOutBatch.splice(index)
          }
        }
      })
    }
    filteredCowsOutBatch.push(cob)
  }

  // 9. Evaluate cases eventually found outside the selected batches
  if (filteredCowsOutBatch.length > 0) {
    if (filteredCowsOutBatch.length === 1) {
      const cow = filteredCowsOutBatch[0]
      const batch = find(batches, { id: cow.loteId })
      const protocol = await getElement(
        AVAILABLE_ENTITIES.PROTOCOLS,
        batch.protocoloId
      )

      let dia = !isEmpty(cow.condicaoDG)
        ? 'DG'
        : !isEmpty(cow.condicaoDGFinal)
          ? 'DG Final'
          : ''

      if (isEmpty(dia)) {
        const days = await getDomainValuesBy('Dias')
        const day = days.find((x) => x.id === batch.dias)
        dia = day ? day.valor : 'D0'
      }

      result.msg = MSG.NOT_IN_BATCH(
        cow.codVaca,
        batch.nomeLote,
        protocol?.name ?? '(protocolo não disponível)',
        dia
      )
      result.data = [cow]
    } else {
      result.data = filteredCowsOutBatch
        .filter((item) => item.iatf === 0)
        .map((i) => {
          const lotName = batches
            .filter((batch) => batch.id === i.loteId)
            .map((t) => t.nomeLote)[0]

          return {
            ...i,
            nomeLote: lotName,
          }
        })
      result.msg = MSG.MORE_THAN_ONE_OUTSIDE_BATCH(cowCode)
    }
  }

  return result
}

export async function findFromAllCows(cowCode, batchId, farmId) {
  let result = {
    data: null,
    msg: null,
  }

  const corrals = await getWithParam(
    AVAILABLE_ENTITIES.CORRALS,
    'farm_id',
    farmId
  )

  const batches = await getWithParam(
    AVAILABLE_ENTITIES.BATCHES,
    'corral_id',
    Q.oneOf(corrals.map((x) => x.id))
  )

  const batchesIds = batches.map((x) => x.id)

  const relatedBatches = filter(
    batches,
    (candidate) => candidate.originalBatchId === batchId
  )
  const relatedBatchesIds = relatedBatches.map((x) => x.id)

  const cowsCollection = database.collections.get(
    AVAILABLE_ENTITIES.D0S.toLowerCase()
  )

  const cowsEntities = await cowsCollection
    .query(
      Q.where('batch_id', Q.oneOf(batchesIds)),
      Q.where('codVaca', Q.like('%' + Q.sanitizeLikeString(cowCode.trim()) + '%')),
      Q.where('isDeleted', false),
      Q.where('omit_from_dg_final', false)
    )
    .fetch()

  const allFilteredCows = cowsEntities.map((entity) => ({
    ...toDTO(AVAILABLE_DTO_MAPPINGS[AVAILABLE_ENTITIES.D0S], entity),
  }))

  // Not Found
  if (allFilteredCows.length === 0) {
    result.msg = MSG.NOT_FOUND
    return result
  }

  const [cowsInBatch, cowsOutBatch] = partition(
    allFilteredCows,
    (x) => x.loteId === batchId || relatedBatchesIds.includes(x.loteId)
  )

  // Found in Batch
  if (cowsInBatch.length > 0) {
    result.data = cowsInBatch

    const unique = uniqBy(cowsInBatch, 'codVaca')

    if (unique.length === 1 && unique[0].loteId !== batchId) {
      // Pregnancy verification
      const cow = unique[0]

      const prenheDG = (await getDomainValuesBy('Condição DG')).filter(
        (x) => x.valor.toLowerCase().indexOf('prenhe') > 0
      )
      const prenheDGFinal = (
        await getDomainValuesBy('Condição DG Final')
      ).filter((x) => x.valor.toLowerCase().indexOf('prenhe') > 0)

      const isPrenhe =
        prenheDG.find((x) => x.id === cow.condicaoDG) ||
        prenheDGFinal.find((x) => x.id === cow.condicaoDGFinal)

      if (isPrenhe) {
        result.msg = MSG.PREGNANT
      }
    }

    return result
  }

  // Found Outside only
  if (cowsOutBatch.length > 0) {
    const unique = uniqBy(cowsOutBatch, 'codVaca')

    if (unique.length === 1) {
      const cow = unique[0]
      const batch = batches.find((x) => x.id === cow.loteId)
      const protocol = await getElement(
        AVAILABLE_ENTITIES.PROTOCOLS,
        batch.protocoloId
      )

      let dia = !isEmpty(cow.condicaoDG)
        ? 'DG'
        : !isEmpty(cow.condicaoDGFinal)
          ? 'DG Final'
          : ''

      if (isEmpty(dia)) {
        const days = await getDomainValuesBy('Dias')
        const day = days.find((x) => x.id === batch.dias)
        dia = day ? day.valor : 'D0'
      }

      result.msg = MSG.NOT_IN_BATCH(
        cow.codVaca,
        batch.nomeLote,
        protocol?.name ?? '(protocolo não disponível)',
        dia
      )
    } else {
      result.msg = MSG.MORE_THAN_ONE_OUTSIDE_BATCH(cowCode)
    }
  }

  return result
}

export function toMoment(date) {
  if (isEmpty(date) && !isNumber(date)) {
    return null
  }
  let m = moment.utc(date)
  if (!m.isValid()) {
    m = moment.utc(Number(date))
  }
  if (m.isValid()) {
    return m
  }
  return null
}

export function fromMoment(m) {
  if (!m) {
    return null
  }
  if (m.isValid()) {
    return m.valueOf()
  }
  return null
}
